<template>
  <b-modal size="lg" id="modal-shift-schedule" title="Tambah Shift Baru" centered>
    <div class="bg-$white p-4 rounded shadow-sm">
      <FormulateForm class="mc-shift-form">
        <div class="row mb-3">
          <div class="col-md-12 mb-3">
            <label class="formulate-input-label">
              {{ $t('setting.shift.shiftName') }}
            </label>
            <div class="mc-shift-input-group">
              <input
                v-model="form.name"
                type="text"
                placeholder="Nama Shift"
                class="form-control mc-shift-input"
              />
              <b-dropdown
                variant="outline-secondary"
                no-caret
                right
                class="mc-shift-color-picker-dropdown"
              >
                <template #button-content>
                  <span class="mc-shift-color-dot" :style="{ backgroundColor: form.color }"></span>
                  <i class="mdi mdi-chevron-down"></i>
                </template>
                <div class="mc-shift-color-picker-content">
                  <div class="mc-shift-color-grid">
                    <span
                      v-for="color in colorOptions"
                      :key="color"
                      class="mc-shift-color-dot mc-shift-color-dot--clickable"
                      :class="{ 'mc-shift-color-dot--selected': form.color === color }"
                      :style="{ backgroundColor: color }"
                      @click="selectColor(color)"
                    ></span>
                    <label
                      class="mc-shift-color-dot mc-shift-color-dot--clickable mc-shift-color-dot--rainbow"
                    >
                      <input
                        v-model="form.color"
                        type="color"
                        class="mc-shift-color-input"
                        @input="onCustomColor"
                      />
                    </label>
                  </div>
                </div>
              </b-dropdown>
            </div>
          </div>

          <div class="mc-shift-time-picker col-md-12">
            <div class="col-md-5">
              <FormulateInput
                v-model="form.startTime"
                type="date-picker"
                format="HH:mm"
                value-type="HH:mm"
                :label="$t('global.object.startTime')"
                placeholder="00:00"
                outer-class="mb-0"
                datepickerType="time"
                icon="icon-outline_clock"
                :input-class="{ 'is-invalid': hasOverlappingShift }"
              />
              <small v-if="hasOverlappingShift" class="text-danger">
                {{ $t('setting.shift.validation.timeExisted') }}
              </small>
            </div>

            <div class="mc-shift-time-arrow col-md-2">
              <i class="mdi mdi-arrow-right fs-4 text-muted"></i>
            </div>

            <div class="col-md-5">
              <FormulateInput
                v-model="form.endTime"
                type="date-picker"
                format="HH:mm"
                value-type="HH:mm"
                :label="$t('global.object.endTime')"
                placeholder="00:00"
                outer-class="mb-0"
                datepickerType="time"
                icon="icon-outline_clock"
                :input-class="{ 'is-invalid': hasOverlappingShift }"
              />

              <small v-if="hasOverlappingShift" class="text-danger">
                {{ $t('setting.shift.validation.timeExisted') }}
              </small>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <label class="formulate-input-label">
              {{ $t('setting.shift.repetition') }}
            </label>
            <div class="mc-modal__filter-button-group">
              <button
                v-for="day in weekdays"
                :key="day.key"
                type="button"
                :class="{
                  'mc-modal__filter-button--active': form.selectedDays.includes(day.key),
                }"
                class="mc-modal__filter-button"
                @click="toggleDay(day.key)"
              >
                {{ day.label }}
              </button>
            </div>
          </div>
        </div>
      </FormulateForm>
    </div>
    <template #modal-footer="{ cancel }">
      <b-row class="mc-modal__filter-footer mx-0">
        <b-col sm="12" md="12" class="mc-modal__filter-footer-btn">
          <McButton
            variant="outline-primary"
            @click="cancel()"
            class="me-2"
            :text="$t('global.action.cancel')"
          />
          <McButton variant="primary" @click="saveShift" :text="$t('global.save')" />
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
import { CREATE_SHIFT } from '@/services/api/setting/shift/shift'
import McButton from '@/components/McButton'

export default {
  name: 'ModalAddShift',
  components: {
    McButton,
  },
  data() {
    return {
      form: {
        name: '',
        startTime: '00:00',
        endTime: '',
        selectedDays: [],
        color: '#F9A825',
      },
      colorOptions: [
        '#BDBDBD',
        '#FF9800',
        '#FDD835',
        '#66BB6A',
        '#42A5F5',
        '#EC407A',
        '#AB47BC',
        '#1E88E5',
        '#6D4C41',
      ],
    }
  },

  computed: {
    weekdays() {
      return [
        { key: 1, label: this.$t('global.days.monday') },
        { key: 2, label: this.$t('global.days.tuesday') },
        { key: 3, label: this.$t('global.days.wednesday') },
        { key: 4, label: this.$t('global.days.thursday') },
        { key: 5, label: this.$t('global.days.friday') },
        { key: 6, label: this.$t('global.days.saturday') },
        { key: 7, label: this.$t('global.days.sunday') },
      ]
    },

    hasOverlappingShift() {
      if (!this.form.startTime || !this.form.endTime || !this.form.selectedDays?.length) {
        return false
      }

      const newStart = this.timeToMinutes(this.form.startTime)
      const newEnd = this.timeToMinutes(this.form.endTime)
      const isNewOvernight = newEnd <= newStart

      return this.transformedShifts.some((shift) => {
        const hasDayOverlap = this.form.selectedDays.some((day) => shift.days.includes(day))
        if (!hasDayOverlap) return false

        const shiftStart = this.timeToMinutes(shift.startTime)
        const shiftEnd = this.timeToMinutes(shift.endTime)

        if (shift.isOvernight) {
          return newStart < shiftEnd || newEnd > shiftStart || isNewOvernight
        }

        return newStart < shiftEnd && newEnd > shiftStart
      })
    },

    existingShifts() {
      return this.$store.getters['shift/shifts'] || []
    },

    transformedShifts() {
      return this.existingShifts.map((shift) => ({
        id: shift.shiftId,
        name: shift.name,
        color: shift.color,
        days: shift.shiftTime?.days || [],
        startTime: shift.shiftTime?.startTime?.slice(0, 5) || '',
        endTime: shift.shiftTime?.endTime?.slice(0, 5) || '',
        isOvernight: this.checkOvernight(shift.shiftTime?.startTime, shift.shiftTime?.endTime),
      }))
    },
  },

  methods: {
    checkOvernight(start, end) {
      if (!start || !end) return false
      return end.slice(0, 5) < start.slice(0, 5)
    },

    toggleDay(day) {
      const index = this.form.selectedDays.indexOf(day)
      if (index > -1) {
        this.form.selectedDays = this.form.selectedDays.filter((d) => d !== day)
      } else {
        this.form.selectedDays = [...this.form.selectedDays, day]
      }
    },

    selectColor(color) {
      this.form.color = color
    },

    onCustomColor(event) {
      this.form.color = event.target.value
    },

    timeToMinutes(time) {
      const [hours, minutes] = time.split(':').map(Number)
      return hours * 60 + minutes
    },

    toUTC(time) {
      if (!time) return null
      const [hours, minutes] = time.split(':').map(Number)
      const date = new Date(Date.UTC(1970, 0, 1, hours, minutes, 0))
      return date.toISOString().slice(11, 19)
    },

    async saveShift() {
      const input = {
        color: this.form.color.slice(1),
        name: this.form.name,
        shiftTime: {
          days: this.form.selectedDays,
          endTime: this.toUTC(this.form.endTime),
          startTime: this.toUTC(this.form.startTime),
        },
      }

      const res = await this.$apollo.mutate({
        mutation: CREATE_SHIFT,
        variables: {
          input,
        },
      })

      if (res.data.createShift.isSuccessful) {
        this.$mcToast.success(this.$t('global.alert.text.added'))
        this.$bvModal.hide('modal-shift-schedule')
      } else {
        this.$mcToast.error(this.$t('global.alert.text.failSave'))
      }
    },

    handleCancel() {
      this.$bvModal.hide('modal-shift-schedule')
    },
  },
}
</script>
<style lang="scss" scoped>
/* Form Label */
.mc-shift-form {
  .formulate-input-label {
    font-weight: 600;
    font-size: 14px;
    color: $teal-700;
    margin-bottom: 0.5rem;
    display: block;
  }
}

// Shift Form Styles
.mc-shift-input-group {
  display: flex;
  align-items: stretch;
  height: 38px; // Fixed height to match time picker

  .mc-shift-input {
    flex: 1;
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    border-right: 0 !important;
    height: 100%;

    &.is-invalid {
      border-color: $red !important;
    }
  }
}

.mc-shift-error-text {
  color: $red;
  font-size: 12px;
  display: block;
  margin-top: 4px;
}

.mc-shift-time-picker {
  display: flex;
  align-items: flex-start;

  .is-invalid {
    .mx-input,
    input {
      border-color: $red !important;
    }
  }
}

.mc-shift-time-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 26px;
}

.mc-modal__filter-button-group {
  &.is-invalid {
    .mc-modal__filter-button {
      border-color: $red;
    }
  }
}

.mc-shift-input,
.mc-shift-input-group input,
.mc-shift-time-picker input,
.mx-input,
.form-control,
input[type='text'],
input[type='time'] {
  caret-color: $black !important;
  color: $black !important;
}

.mx-datepicker {
  input {
    caret-color: $black !important;
    color: $black !important;
  }
}

.bg-white {
  input {
    caret-color: $black !important;
    color: $black !important;
  }
}

.mc-modal__filter-button {
  &--error {
    border-color: $red !important;
  }
}

/* Color Picker Dropdown - flat left side */
.mc-shift-color-picker-dropdown {
  .btn,
  .btn.dropdown-toggle,
  button.btn {
    border-radius: 0 4px 4px 0 !important; /* top-left, top-right, bottom-right, bottom-left */
    border-color: $teal-100;
    border-left: none !important;
    background: $white;
    display: flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 12px;

    &:hover,
    &:focus {
      background: $teal-100;
    }
  }

  .dropdown-menu {
    min-width: auto;
    padding: 0;
    border-radius: 12px;
    overflow: hidden;
  }

  .mdi-chevron-down {
    vertical-align: top;
    margin-left: 3px;
  }
}

.mc-shift-color-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  width: calc(5 * 20px + 4 * 12px);
}

/* Color Dots */
.mc-shift-color-dot {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  min-height: 20px !important;
  max-width: 20px !important;
  max-height: 20px !important;
  aspect-ratio: 1 / 1 !important;
  border-radius: 50% !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  flex-grow: 0;
  box-sizing: border-box;

  &--clickable {
    cursor: pointer;
    transition: transform 0.2s, border-color 0.2s;

    &:hover {
      transform: scale(1.15);
    }
  }

  &--selected {
    border-color: $teal-100;

    &::after {
      content: '✓';
      color: $white;
      font-size: 14px;
      font-weight: bold;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    }
  }

  &--rainbow {
    background-image: url('~@/assets/images/color-wheel.png');
    background-size: 115%;
    background-position: center 40%;
    position: relative;
    overflow: hidden;
    border: none;

    .mc-shift-color-input {
      opacity: 0;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
    }
  }
}

/* Rainbow Color Picker Content */
.mc-shift-color-picker-content {
  padding: 16px;
  background: $white;
  border-radius: 12px;
}
</style>